import React, {useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {ImagePlaceholder} from '@vio/atlas/helpers/ImagePlaceholder'
import useIsImageloaded from '@vio/atlas/hooks/useIsImageloaded'
import {ImageSources} from '@vio/atlas/Image'
import {SKELETON_BASE_COLOR, SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {cssTheme} from '@vio/atlas/themes'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

const FadeInImage = styled.div<{isLoaded: boolean; skipFade?: boolean}>`
  opacity: ${({isLoaded, skipFade}) => (skipFade ? 1 : isLoaded ? 1 : 0)};
  transition: ${({skipFade}) =>
    skipFade ? 'none' : 'opacity 0.3s ease-in-out'};
  width: 100%;
  height: 100%;
  background-color: ${SKELETON_BASE_COLOR};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    position: absolute;
    top: 0;
    left: 0;
    display: block;
  }
  &.--with-placeholder {
    position: relative;
    background-color: ${cssTheme.colors.imagePlaceholder.background};
    color: ${cssTheme.colors.imagePlaceholder.color};
  }
`
interface Props {
  sources: ImageSources
  loading: 'eager' | 'lazy'
  isPriorityImage?: boolean
}

export const SlideImageWithSkeleton = ({
  sources,
  loading,
  isPriorityImage = false
}: Props) => {
  const isHydrated = useIsHydrated()
  // Capture whether we're in the initial server/first-client render
  // to skip loading states for better perceived performance
  const [skipLoading] = useState(!isHydrated)

  const {isLoaded, isErrored} = useIsImageloaded(sources.src, sources.srcSet)
  return (
    <>
      <FadeInImage
        isLoaded={isLoaded || isErrored}
        skipFade={isPriorityImage || skipLoading}
        className={cx(isErrored && '--with-placeholder')}
      >
        {isErrored ? (
          <ImagePlaceholder type="hotel" />
        ) : (
          //eslint-disable-next-line @next/next/no-img-element
          <img
            src={sources.src}
            srcSet={sources.srcSet || undefined}
            loading={loading}
            // eslint-disable-next-line react/no-unknown-property
            fetchPriority={isPriorityImage ? 'high' : 'auto'}
            alt=""
          />
        )}
      </FadeInImage>
      {!skipLoading && !isLoaded && !isErrored && (
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          zIndex="1"
        >
          <SkeletonLoader height="100%" />
        </Box>
      )}
    </>
  )
}
